import {
  type PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

import { BRAND_TOKENS, DEFAULT_PRESET, type PresetKey, PRESETS } from "../lib/theme/presets";
import { ThemeContext, type ThemePreference } from "./theme-context";

const storageKey = "burgepos:ui-theme:v1";

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getInitialPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  } catch {
    return "system";
  }
}

function storePreference(preference: ThemePreference) {
  try {
    window.localStorage.setItem(storageKey, preference);
  } catch {
    // A blocked storage API must not prevent theme selection for this session.
  }
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [preference, setPreferenceState] = useState<ThemePreference>(getInitialPreference);
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(getSystemTheme);
  const [brand, setBrand] = useState<PresetKey>(DEFAULT_PRESET);
  const resolvedTheme = preference === "system" ? systemTheme : preference;

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const updateSystemTheme = () => setSystemTheme(media.matches ? "dark" : "light");
    media.addEventListener("change", updateSystemTheme);
    return () => media.removeEventListener("change", updateSystemTheme);
  }, []);

  // Before paint, as the business's colours are, so the two never disagree
  // for a frame.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.style.colorScheme = resolvedTheme;
    storePreference(preference);
  }, [preference, resolvedTheme]);

  // The business's accent family, over the stylesheet's, before the browser
  // paints, so the shell never shows the default first. The default preset is
  // the stylesheet itself, so it sets nothing and leaves both appearances to
  // index.css (which read-document.ts relies on).
  useLayoutEffect(() => {
    const style = document.documentElement.style;
    for (const token of BRAND_TOKENS) {
      if (brand === DEFAULT_PRESET) {
        style.removeProperty(`--${token}`);
      } else {
        style.setProperty(`--${token}`, PRESETS[brand][resolvedTheme][token]);
      }
    }
  }, [brand, resolvedTheme]);

  const value = useMemo(
    () => ({
      preference,
      resolvedTheme,
      setBrand,
      setPreference: setPreferenceState,
    }),
    [preference, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
